import { minIncomeToLive } from '@algoan/dashboard/env';
import { GroupedCashFlows, Customer, MaritalStatus, singleMaritalStatuses } from '@algoan/dashboard/model';
import {
  calculateCashFlow,
  calculateLastFullCompleteMonth,
  sumCategoriesForGivenMonth,
  sumCategoriesTotal,
} from './cash-flow-calculator';

export interface RemainderToLiveValues {
  remainderToLive: number;
  minimumIncomeToLive?: number;
  realRemainderToLive?: number;
  remainderToLivePerPerson?: number;
}

export const calculateRemainderToLiveValues = (
  checkedCashFlows: { [key: number]: boolean },
  regularCashFlowAnalysis: GroupedCashFlows,
  customer: Customer,
): RemainderToLiveValues => {
  const remainderToLive = getRemainderToLiveForSpecificMonthOrLastCompleteMonth(
    regularCashFlowAnalysis,
    checkedCashFlows,
  );

  if (typeof customer.personalDetails?.household?.maritalStatus !== 'undefined') {
    const nbOfDependantPersons =
      (customer.personalDetails?.household?.numberOfDependentChildren ?? 0) +
      (customer.personalDetails?.household?.numberOfOtherDependents ?? 0);
    const minimumIncomeToLive = getMinimumIncomeToLive(
      nbOfDependantPersons,
      customer.personalDetails?.household?.maritalStatus,
    );

    const realRemainderToLive = remainderToLive - minimumIncomeToLive;

    const consumerUnits =
      1 +
      ((singleMaritalStatuses.indexOf(customer.personalDetails?.household?.maritalStatus) > -1 ? 0 : 1) +
        (customer.personalDetails?.household?.numberOfOtherDependents ?? 0)) *
        0.5 +
      (customer.personalDetails?.household?.numberOfDependentChildren ?? 0) * 0.3;

    const remainderToLivePerPerson = remainderToLive / consumerUnits;

    return {
      remainderToLive,
      minimumIncomeToLive,
      realRemainderToLive,
      remainderToLivePerPerson,
    };
  }

  return { remainderToLive };
};

const getMinimumIncomeToLive = (nbOfDependantPersons: number, maritalStatus: MaritalStatus): number => {
  const {
    singleWithoutChildren,
    coupleWithoutChildren,
    singleWithOneChild,
    coupleWithOneChild,
    singleWithTwoChildren,
    coupleWithTwoChildren,
    singleAdditionalPerson,
    coupleAdditionalPerson,
  } = minIncomeToLive;
  const isSingle = singleMaritalStatuses.includes(maritalStatus);

  switch (nbOfDependantPersons) {
    case 0:
      return isSingle ? singleWithoutChildren : coupleWithoutChildren;

    case 1:
      return isSingle ? singleWithOneChild : coupleWithOneChild;

    case 2:
      return isSingle ? singleWithTwoChildren : coupleWithTwoChildren;

    default:
      const minimumIncomeToLiveWithTwoChildren = getMinimumIncomeToLive(2, maritalStatus);
      const additionalChildren = nbOfDependantPersons - 2;

      const getAdditionalMinimumIncomeToLive = (additionalMinIncomeToLivePerson: number): number =>
        minimumIncomeToLiveWithTwoChildren + additionalChildren * additionalMinIncomeToLivePerson;

      return isSingle
        ? getAdditionalMinimumIncomeToLive(singleAdditionalPerson)
        : getAdditionalMinimumIncomeToLive(coupleAdditionalPerson);
  }
};

export const getRemainderToLiveForSpecificMonthOrLastCompleteMonth = (
  groupedCashFlows: GroupedCashFlows,
  checkedCashFlows: { [key: number]: boolean },
  month?: string,
  newLoanInput?: number,
): number => {
  const lastFullCompleteMonth = calculateLastFullCompleteMonth(groupedCashFlows);

  const calculatedCashFlow = sumCategoriesForGivenMonth(
    groupedCashFlows,
    checkedCashFlows,
    month || lastFullCompleteMonth.month,
  );

  const newLoan = newLoanInput ? newLoanInput : 0;

  return (
    calculateCashFlow({
      incomes: calculatedCashFlow.checkedIncomes,
      resources: calculatedCashFlow.checkedResources,
      fixedCharges: calculatedCashFlow.checkedFixedCharges,
      expenses: calculatedCashFlow.checkedExpenses,
      loans: calculatedCashFlow.checkedLoans,
    }) - newLoan
  );
};

export const monthHasLoanMonthlyAmount = (
  month: string,
  groupedCashFlows: GroupedCashFlows,
  checkedCashFlows: { [key: number]: boolean },
): boolean => {
  const calculatedCashFlow = sumCategoriesForGivenMonth(groupedCashFlows, checkedCashFlows, month);

  return !!calculatedCashFlow.checkedLoans;
};

export const getRemainderToLiveTotal = (
  groupedCashFlows: GroupedCashFlows,
  checkedCashFlows: { [key: number]: boolean },
  newLoanTotal?: number,
): number => {
  const totalCashFlow = sumCategoriesTotal(groupedCashFlows, checkedCashFlows);

  const total = newLoanTotal ? newLoanTotal : 0;

  return calculateCashFlow({
    incomes: totalCashFlow.checkedIncomes,
    resources: totalCashFlow.checkedResources,
    fixedCharges: totalCashFlow.checkedFixedCharges,
    expenses: totalCashFlow.checkedExpenses,
    loans: totalCashFlow.checkedLoans + total,
  });
};
